import type { CompiledGameArtifact } from "../shared/adapter";
import { BRIDGE_MESSAGE_TYPE, SANDBOX_CSP, safeJson } from "../shared/runtime";
import { SOURCE_GAME_INPUT_RUNTIME } from "./input-runtime";
import type { SourceGameSpec } from "./spec";

function escapeStyle(value: string): string {
  return value.replace(/<\/style/gi, "<\\/style");
}

function escapeScript(value: string): string {
  return value.replace(/<\/script/gi, "<\\/script").replace(/<!--/g, "<\\!--");
}

export function compileSourceGame(spec: SourceGameSpec): CompiledGameArtifact {
  const trustedBootstrap = `
const root=document.getElementById('carcade-game-root');
let rng=${spec.seed >>> 0},readySent=false;
const post=(type,payload={})=>{try{parent.postMessage({type,...payload},'*')}catch{}};
const showError=(value)=>{
  const message=value instanceof Error?value.message:String(value||'Unknown game error');
  let panel=document.getElementById('carcade-runtime-error');
  if(!panel){panel=document.createElement('div');panel.id='carcade-runtime-error';panel.setAttribute('role','alert');document.body.append(panel)}
  panel.textContent='The game stopped: '+message;
  post('carcade-error-v1',{message});
};
${SOURCE_GAME_INPUT_RUNTIME}
const pauseListeners=new Set();
const api=Object.freeze({
  root,
  input:Object.freeze({create:definitions=>createCarcadeInputRuntime(root,post,definitions)}),
  seed:${spec.seed},
  random(){rng=(rng*1664525+1013904223)>>>0;return rng/4294967296},
  ready(detail={}){if(readySent)return;readySent=true;post('carcade-ready-v1',{detail})},
  state(state){post('${BRIDGE_MESSAGE_TYPE}',{state})},
  event(name,detail={}){post('carcade-event-v1',{name,detail})},
  onPause(listener){if(typeof listener!=='function')throw new TypeError('pause listener must be a function');pauseListeners.add(listener);return()=>pauseListeners.delete(listener)}
});
const testPointers=new Map();
function dispatchPhasedTestInput(action){
  if(!action||typeof action!=='object')return;
  if(action.type==='pointer'){
    if(!['down','move','up','cancel'].includes(action.phase)||!Number.isInteger(action.pointerId)||action.pointerId<0||!Number.isFinite(action.x)||!Number.isFinite(action.y))return;
    const x=Math.max(0,Math.min(1,action.x))*innerWidth,y=Math.max(0,Math.min(1,action.y))*innerHeight;
    if(action.phase==='down'){
      if(testPointers.has(action.pointerId))return;
      testPointers.set(action.pointerId,document.elementFromPoint(x,y)||root);
    }
    const target=testPointers.get(action.pointerId);if(!target)return;
    if(typeof PointerEvent==='function')target.dispatchEvent(new PointerEvent('pointer'+action.phase,{bubbles:true,cancelable:true,pointerId:action.pointerId,pointerType:'touch',isPrimary:action.pointerId===1,clientX:x,clientY:y,button:0,buttons:action.phase==='up'||action.phase==='cancel'?0:1}));
    else if(typeof Touch==='function'&&typeof TouchEvent==='function'){
      const touch=new Touch({identifier:action.pointerId,target,clientX:x,clientY:y});
      target.dispatchEvent(new TouchEvent('touch'+({down:'start',move:'move',up:'end',cancel:'cancel'}[action.phase]),{bubbles:true,cancelable:true,changedTouches:[touch]}));
    }
    if(action.phase==='up'||action.phase==='cancel')testPointers.delete(action.pointerId);
    return;
  }
  if(action.type==='key'&&['down','up'].includes(action.phase)&&typeof action.key==='string'&&typeof action.code==='string'){
    document.body.dispatchEvent(new KeyboardEvent('key'+action.phase,{key:action.key,code:action.code,bubbles:true,cancelable:true}));
  }
}
function dispatchTestInput(action){
  if(!action||typeof action!=='object')return;
  if(action.type==='pointer'){
    const x=Math.max(0,Math.min(1,Number(action.x)||0.5))*innerWidth;
    const y=Math.max(0,Math.min(1,Number(action.y)||0.5))*innerHeight;
    const target=document.elementFromPoint(x,y)||root;
    const PointerCtor=typeof PointerEvent==='function'?PointerEvent:MouseEvent;
    for(const type of ['pointerdown','pointerup','click']){
      const event=new PointerCtor(type,{bubbles:true,cancelable:true,clientX:x,clientY:y,button:0,buttons:type==='pointerdown'?1:0});
      target.dispatchEvent(event);
    }
    return;
  }
  if(action.type==='key'&&typeof action.key==='string'){
    const target=document.activeElement&&document.activeElement!==document.body?document.activeElement:document.body;
    target.dispatchEvent(new KeyboardEvent('keydown',{key:action.key,bubbles:true,cancelable:true}));
    target.dispatchEvent(new KeyboardEvent('keyup',{key:action.key,bubbles:true,cancelable:true}));
  }
}
function visibleCanvas(){
  let best=null,bestArea=0;
  for(const canvas of root.querySelectorAll('canvas')){
    const rect=canvas.getBoundingClientRect(),area=rect.width*rect.height;
    if(area>bestArea&&rect.width>40&&rect.height>40){best=canvas;bestArea=area}
  }
  return best;
}
async function captureVisual(requestId){
  const text=(document.body.innerText||'').replace(/\s+/g,' ').trim().slice(0,6000);
  try{
    const width=Math.max(1,Math.min(960,Math.round(innerWidth||960)));
    const height=Math.max(1,Math.min(540,Math.round(innerHeight||540)));
    const shot=document.createElement('canvas');shot.width=width;shot.height=height;
    const ctx=shot.getContext('2d');if(!ctx)throw new Error('capture canvas unavailable');
    const canvas=visibleCanvas();
    if(canvas){
      ctx.fillStyle=getComputedStyle(document.body).backgroundColor||'#000';ctx.fillRect(0,0,width,height);
      ctx.drawImage(canvas,0,0,width,height);
    }else{
      const serialized=new XMLSerializer().serializeToString(root.cloneNode(true));
      const css=Array.from(document.querySelectorAll('style')).map(node=>node.textContent||'').join('\\n');
      const svg='<svg xmlns="http://www.w3.org/2000/svg" width="'+width+'" height="'+height+'"><foreignObject width="100%" height="100%"><div xmlns="http://www.w3.org/1999/xhtml" style="width:'+width+'px;height:'+height+'px;overflow:hidden"><style>'+css.replace(/<\\/style/gi,'<\\\\/style')+'</style>'+serialized+'</div></foreignObject></svg>';
      const image=new Image();image.src='data:image/svg+xml;charset=utf-8,'+encodeURIComponent(svg);
      if(typeof image.decode==='function')await image.decode();else await new Promise((resolve,reject)=>{image.onload=resolve;image.onerror=reject});
      ctx.drawImage(image,0,0,width,height);
    }
    post('carcade-visual-v1',{requestId,dataUrl:shot.toDataURL('image/jpeg',0.68),text});
  }catch(error){
    post('carcade-visual-v1',{requestId,text,error:error instanceof Error?error.message:String(error)});
  }
}
addEventListener('message',event=>{
  if(event.source!==parent)return;
  const data=event.data;if(!data||typeof data!=='object')return;
  if(data.type==='carcade-control-v1'&&(data.action==='pause'||data.action==='resume')){
    for(const instance of activeInputInstances)instance.releaseAll(data.action);
    testPointers.clear();
    for(const listener of pauseListeners){try{listener(data.action)}catch(error){showError(error)}}
    return;
  }
  if(data.type==='carcade-discover-controls-v1'){publishInputControls();return}
  if(data.type==='carcade-test-input-v2'){dispatchPhasedTestInput(data.action);return}
  if(data.type==='carcade-test-input-v1'){dispatchTestInput(data.action);return}
  if(data.type==='carcade-capture-v1'){void captureVisual(typeof data.requestId==='string'?data.requestId:'capture');}
});
addEventListener('error',event=>showError(event.error||event.message));
addEventListener('unhandledrejection',event=>showError(event.reason));
try{
(function($CARCADE){
${escapeScript(spec.source.script)}
})(api);
requestAnimationFrame(()=>api.ready({title:${safeJson(spec.title)}}));
}catch(error){showError(error)}
`;
  return {
    contentType: "text/html; charset=utf-8",
    html: `<!doctype html>
<html lang="en"><head><meta charset="utf-8"><meta name="viewport" content="width=device-width,initial-scale=1,viewport-fit=cover">
<meta http-equiv="Content-Security-Policy" content="${SANDBOX_CSP}">
<title>${spec.title.replace(/[<>&]/g, "")}</title><style>
html,body{margin:0;width:100%;height:100%;overflow:hidden;background:#05060a;color:#fff;font-family:system-ui,-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif;-webkit-user-select:none;user-select:none;-webkit-tap-highlight-color:transparent}
*,*::before,*::after{box-sizing:border-box}button,input,select,textarea{font:inherit}button{touch-action:manipulation}canvas{display:block;max-width:100%;max-height:100%;touch-action:none}
#carcade-game-root{position:relative;width:100%;height:100%;overflow:hidden}
#carcade-runtime-error{position:fixed;z-index:2147483647;inset:auto 16px 16px;padding:14px 18px;border:2px solid #ff776d;border-radius:14px;background:#24090dcc;color:#fff;font-weight:750;box-shadow:0 8px 32px #0008}
${escapeStyle(spec.source.styles)}
</style></head><body><main id="carcade-game-root">${spec.source.markup}</main><script>(()=>{
${trustedBootstrap}
})();</script></body></html>`,
  };
}
